import CustomConfig, { CustomConfigProps } from '../configuration/customConfig';

/**
 * Removes params from the URL according to the allowedURLParams and ignoredURLParams in the config
 * If allowedURLParams is nonempty, all params *not* in the list are removed
 * Else if ignoredURLParams is nonempty, and allowedURLParams is empty, all params in ignoredURLParams are removed
 * Else *all* params are allowed (only happens when both allowedURLParams and ignoredURLParams are empty).
 * @param url - The URL to clean.
 * @param overrideConfig - A CustomConfigProps that overrides the one parsed from the appropriate AWSMA element
 * @return The cleaned URL
 */
export const stripOutUnwantedParams = function (url: string, overrideConfig?: CustomConfigProps): string {
  try {
    const urlObj = new URL(url);
    if (!urlObj.search) {
      return url;
    }

    const effectiveConfig = overrideConfig || CustomConfig.config;

    const paramFilterFunc = getParamFilterFunc(effectiveConfig);
    const filteredParams: URLSearchParams = getFilteredParamsFromUrl(urlObj, paramFilterFunc);
    urlObj.search = filteredParams.toString();

    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

/**
 * NOTE: Exported for testing purposes only - treat as a private method in production code
 * Returns a function that checks param names and returns true if they're allowed, and false otherwise.
 * @param config - The CustomConfigProps value to use for determining allowedURLParams and ignoredURLParams.
 * @return A function that checks param names and returns true if they're allowed, and false otherwise.
 */
export const getParamFilterFunc = function (config: CustomConfigProps): (a: string) => boolean {
  const allowed = config.allowedURLParams;
  const ignored = config.ignoredURLParams;

  if (allowed && allowed.length) {
    return (it: string) => allowed.includes(it);
  } else if (ignored && ignored.length) {
    return (it: string) => !ignored.includes(it);
  }

  return () => true;
};

/**
 * NOTE: Exported for testing purposes only - treat as a private method in production code.
 * Retrieves the params from the URL and filters out any that fail the filter function
 * @param url - The URL from which to grab params.
 * @param filterFunc - The function that returns a boolean value for any given string, indicating whether it's allowed or not.
 * @return A query string equivalent to the passed params, reproduced in the same order.
 */

export const getFilteredParamsFromUrl = function (url: URL, filterFunc: (a: string) => boolean): URLSearchParams {
  const urlSearchParams = new URLSearchParams(url.search);
  urlSearchParams.forEach(function (value: string, name: string) {
    if (!filterFunc(name)) {
      urlSearchParams.delete(name);
    }
  });

  return urlSearchParams;
};

/**
 * Validate if the url parameter is in valid URL format
 * @param url the url to validate
 * @returns boolean, true if the url is valid, false otherwise
 */
export const isValidUrl = function (url: string | undefined): boolean {
  if (url === undefined) {
    return false;
  }

  try {
    new URL(url);
  } catch (e: any) {
    return false;
  }

  return true;
};
